import { CSSResult, html, LitElement, PropertyValues, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property } from 'lit/decorators.js';
import { PromotionData } from '@cube/tracking-library-api/dist/@types/events/fragments.js';
import { WishlistMutationEvent } from '../../../@types/wishlist.js';
import css from './wishlist-button.scss?inline';
import { config } from '../../../scripts/service/config.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import { ButtonSize } from '../heart-button/button-size.js';
import { renderHeartIcon } from '../heart-button/heart-icons.js';
import { HeartAnimationController } from '../heart-button/heart-animation-controller.js';

@customElement('ents-wishlist-button')
export class WishlistButtonElement extends LitElement {
  @property({
    type: Array,
    attribute: 'initial-state',
  })
  private initialState: string[] = [];

  @property({ type: Object, attribute: false }) private state: Record<string, boolean> = {};

  @property({ type: Number }) private syan: number = 0;

  @property({ type: String, attribute: 'color-id' }) private colorId?: string = undefined;

  @property({ type: String, attribute: 'article-id' }) private articleId?: string = undefined;

  @property({ type: Boolean, attribute: 'show-text' }) private showText: boolean = false;

  @property({ type: Boolean, attribute: 'no-color' }) private noColor: boolean = false;

  @property({ type: String }) private placement?: string = undefined;

  @property({ type: Object }) private promotion?: PromotionData = undefined;

  @property({ type: Number, attribute: 'position' }) private position?: number = undefined;

  @property({ type: String, attribute: 'size' })
  private size: ButtonSize = 'normal';

  @property({ type: Boolean, attribute: 'notify-on-back-in-stock' }) private notifyOnBackInStock: boolean = false;

  @property({ type: Boolean, reflect: true }) private trigger: boolean = false;

  @property({ type: Boolean, attribute: 'needs-newslettersubscription' })
  private needsNewsletterSubscription: boolean = false;

  @property({ type: Boolean, attribute: false }) private showPlaceholderNewsletterModal: boolean = false;

  private readonly animation = new HeartAnimationController(this);

  // use this syntax to make it work in Safari 14
  // working: static get styles() { return css`...` }
  // not working: static styles = css`...`
  static get styles(): CSSResult {
    return unsafeCSS(css);
  }

  private readonly onWishlistAddedEvent: (e: WishlistMutationEvent) => void;

  private readonly onWishlistRemovedEvent: (e: WishlistMutationEvent) => void;

  private selectedSlotNodes?: NodeListOf<Element> = undefined;

  private unselectedSlotNodes?: NodeListOf<Element> = undefined;

  attributeChangedCallback(name: string, _old: string | null, value: string | null) {
    super.attributeChangedCallback(name, _old, value);
    if (name === 'initial-state') {
      this.state = this.getInitialState();
    }
  }

  constructor() {
    super();

    this.onWishlistAddedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = { ...this.state, ...{ [data.identifier]: true } };
    };

    this.onWishlistRemovedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = { ...this.state, ...{ [data.identifier]: false } };
      this.animation.resetImmediate();
    };

    this.state = {};
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.addEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);

    this.state = this.getInitialState();

    this.selectedSlotNodes = this.querySelectorAll('[slot="selected"]');
    this.unselectedSlotNodes = this.querySelectorAll('[slot="unselected"]');
  }

  private getInitialState() {
    const initialState = this.initialState ?? [];
    return initialState.reduce(
      (agg, v) => ({
        ...agg,
        [v]: true,
      }),
      {},
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.removeEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);
  }

  shouldUpdate(changedProperties: PropertyValues) {
    if (!this.notifyOnBackInStock && this.animation.shouldBlockUpdate()) return false;
    return super.shouldUpdate(changedProperties);
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    this.setAttribute('selected', this.isOnWishlist() ? 'true' : 'false');

    if (changedProperties.has('trigger') && this.trigger) {
      this.addToWishlist();
      this.trigger = false;
    }
  }

  private isOnWishlist() {
    return this.state[ProductIdentifier.create(this.syan, this.colorId).id];
  }

  private getLabel() {
    if (this.isOnWishlist()) {
      return config.messageWishlistTextActive;
    }
    return config.messageWishlistTextInactive;
  }

  private isLoggedIn() {
    return config.currentlyLoggedIn === 'true';
  }

  private handleClick(e: Event) {
    e.stopPropagation();

    if (this.notifyOnBackInStock && this.isOnWishlist()) {
      e.preventDefault();
      return;
    }

    if (this.needsNewsletterSubscription && !this.isOnWishlist()) {
      e.preventDefault();
      if (!this.isLoggedIn()) {
        window.location.href = `${config.loginEndpoint}?referrer=${encodeURIComponent(window.location.href)}`;
      } else {
        this.showPlaceholderNewsletterModal = true;
      }
      return;
    }

    if (this.isOnWishlist()) {
      document.dispatchEvent(
        new CustomEvent('ents:wishlist:remove', {
          detail: {
            identifier: ProductIdentifier.create(this.syan, this.colorId).id,
            syan: this.syan,
            colorId: this.colorId,
            ...(this.articleId && this.articleId !== 'null' ? { articleId: this.articleId } : {}),
            tracking: {
              placement: this.placement,
              promotion: this.promotion,
              context: 'remove_heart',
              position: this.position,
            },
          },
        }),
      );
      return;
    }
    this.addToWishlist();
  }

  private addToWishlist() {
    if (this.isOnWishlist()) {
      return;
    }
    if (!this.notifyOnBackInStock) {
      this.animation.trigger();
    }
    document.dispatchEvent(
      new CustomEvent('ents:wishlist:add', {
        detail: {
          identifier: ProductIdentifier.create(this.syan, this.colorId).id,
          syan: this.syan,
          colorId: this.colorId,
          ...(this.articleId && this.articleId !== 'null' ? { articleId: this.articleId } : {}),
          ...(this.notifyOnBackInStock ? { notifyOnBackInStock: new Date().toISOString() } : {}),
          tracking: {
            placement: this.placement,
            promotion: this.promotion,
            context: this.notifyOnBackInStock ? 'notify_subscribe' : 'add_heart',
            position: this.position,
          },
        },
      }),
    );
  }

  private hasSlottedContent() {
    if (this.selectedSlotNodes === undefined || this.unselectedSlotNodes === undefined) {
      return false;
    }
    return this.selectedSlotNodes.length > 0 && this.unselectedSlotNodes.length > 0;
  }

  private renderSelectedSlot() {
    this.selectedSlotNodes?.forEach((n) => n.removeAttribute('style'));
    return html` <slot name="selected"></slot>`;
  }

  private renderUnselectedSlot() {
    this.unselectedSlotNodes?.forEach((n) => n.removeAttribute('style'));
    return html` <slot name="unselected"></slot>`;
  }

  private renderSlottedContent() {
    return this.isOnWishlist() ? this.renderSelectedSlot() : this.renderUnselectedSlot();
  }

  private renderContent() {
    if (this.hasSlottedContent()) {
      return this.renderSlottedContent();
    }
    const renderLabel = () => {
      if (this.showText) {
        return html`<span class="ents-wishlist-button__text">${this.getLabel()}</span>`;
      }
      return '';
    };
    return html`${renderHeartIcon(this.size, this.isOnWishlist())}${renderLabel()}`;
  }

  protected render() {
    return html`
      <label
        aria-label="${config.messageButtonAriaLabel}"
        @click="${(e: Event) => e.stopPropagation()}"
        class="ents-wishlist-button ${classMap({
          selected: this.isOnWishlist(),
          styled: !this.hasSlottedContent(),
          'no-color': this.noColor,
          text: this.showText,
          pulse: this.animation.active,
          'no-description': !this.showText,
        })}"
      >
        <input
          type="checkbox"
          aria-checked="${this.isOnWishlist()}"
          ?checked="${this.isOnWishlist()}"
          ?disabled="${this.notifyOnBackInStock && this.isOnWishlist()}"
          @click="${this.handleClick}"
          data-test-ents-product-on-wishlist="${this.isOnWishlist()}"
          class="sr-only"
        />
        ${this.renderContent()}
      </label>
      ${
        this.showPlaceholderNewsletterModal
          ? html`<div data-test-ents-newsletter-subscription-placeholder-modal>Newsletter subscription required</div>`
          : ''
      }
    `;
  }
}
